import type { ClientDetail, ClientInput, ClientSummary, ClientTablePage, DuplicateCandidate, TagItem } from "@vaulted/shared";
import { ageOn, callerIdName, formatPhone } from "@vaulted/shared";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { errorMessage, get, post } from "../api";
import { DateOfBirthField, Alert, Button, Dialog, Field, Loading, PageHeader, Panel, SelectField, searchInputProps } from "../components/ui";
import { useSession } from "../session";
import { todayIn } from "./Calendar";

const today = () => todayIn();
export const fmtDob = (dob: string | null) => {
  if (!dob) return "Birth date needed";
  const [y, m, d] = dob.split("-");
  return `${Number(m)}/${Number(d)}/${y}`;
};
export const displayName = (c: Pick<ClientSummary, "firstName" | "lastName" | "preferredName">) =>
  c.preferredName && c.preferredName !== c.firstName ? `${c.firstName} "${c.preferredName}" ${c.lastName}` : `${c.firstName} ${c.lastName}`;

export function useTags() {
  return useQuery({ queryKey: ["tags"], queryFn: () => get<TagItem[]>("/api/tags") });
}

export function TagChips({ ids, tags }: { ids: string[]; tags: TagItem[] | undefined }) {
  if (!tags || !ids.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {ids.map((id) => {
        const t = tags.find((x) => x.id === id);
        if (!t) return null;
        return (
          <span key={id} className="rounded-full px-2 py-0.5 text-xs font-semibold text-white" style={{ background: t.color }}>
            {t.name}
          </span>
        );
      })}
    </span>
  );
}

export function ClientRows({ list, tags, onPick }: { list: ClientSummary[]; tags: TagItem[] | undefined; onPick?: (c: ClientSummary) => void }) {
  return (
    <ul className="divide-y divide-mist">
      {list.map((c) => {
        const body = (
          <>
            <div className="min-w-0">
              <div className="font-semibold">
                {displayName(c)} {c.status === "lead" && <span className="text-xs font-normal text-muted">(lead)</span>}
                {c.status === "archived" && <span className="text-xs font-normal text-muted">(archived)</span>}
              </div>
              <div className="text-sm text-muted">
                {fmtDob(c.dob)}{c.dob && ` · age ${ageOn(c.dob, today())}`}
                {c.mobile && ` · ${formatPhone(c.mobile)}`}
                {c.email && ` · ${c.email}`}
              </div>
            </div>
            <TagChips ids={c.tagIds} tags={tags} />
          </>
        );
        return (
          <li key={c.id}>
            {onPick ? (
              <button type="button" onClick={() => onPick(c)} className="flex w-full flex-wrap items-center justify-between gap-2 px-4 py-3 text-left hover:bg-mist/50">
                {body}
              </button>
            ) : (
              <Link to={`/clients/${c.id}`} className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 hover:bg-mist/50">
                {body}
              </Link>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Debounced client search. */
export function useClientSearch(q: string, archived = false, withArchived = false) {
  const [debounced, setDebounced] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  return useQuery({
    queryKey: ["clients", debounced, archived, withArchived],
    queryFn: () => get<ClientSummary[]>(`/api/clients?q=${encodeURIComponent(debounced)}${archived ? "&archived=1" : ""}${withArchived ? "&withArchived=1" : ""}`),
    enabled: debounced.length >= 2 || archived,
  });
}

type SortKey = "activity" | "name" | "phone" | "lastVisit";
const SORT_LABELS: Record<SortKey, string> = { activity: "Recent activity", name: "Name", phone: "Phone", lastVisit: "Last visit" };
const SORT_STORE = "vaulted.clients.sort";
const fmtVisit = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

export function ClientsPage() {
  const { can } = useSession();
  const tags = useTags();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const [archived, setArchived] = useState(false);
  const [tagId, setTagId] = useState("");
  const [upcoming, setUpcoming] = useState(false);
  // The sort is remembered on this computer.
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>(() => {
    try {
      const v = JSON.parse(localStorage.getItem(SORT_STORE) ?? "null") as { key?: string; dir?: string } | null;
      if (v?.key && v.key in SORT_LABELS && (v.dir === "asc" || v.dir === "desc")) return { key: v.key as SortKey, dir: v.dir };
    } catch {
      // No storage (private window): the default.
    }
    return { key: "activity", dir: "desc" };
  });
  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORE, JSON.stringify(sort));
    } catch {
      // Not saved; the page still works.
    }
  }, [sort]);
  // From the incoming-call pop-up or the call list: add the caller, with their number (and caller ID name) filled in.
  const [params] = useSearchParams();
  const [adding, setAdding] = useState(params.has("add"));
  useEffect(() => {
    if (params.has("add")) setAdding(true);
  }, [params]);
  const query = new URLSearchParams({ q: debounced, sort: sort.key, dir: sort.dir, ...(archived ? { archived: "1" } : {}), ...(tagId ? { tagId } : {}), ...(upcoming ? { upcoming: "1" } : {}) }).toString();
  const pages = useInfiniteQuery({
    queryKey: ["clients-table", query],
    queryFn: ({ pageParam }) => get<ClientTablePage>(`/api/clients/table?${query}&offset=${pageParam}`),
    initialPageParam: 0,
    getNextPageParam: (last, all) => {
      const loaded = all.reduce((n, p) => n + p.rows.length, 0);
      return loaded < last.total ? loaded : undefined;
    },
  });
  const rows = pages.data?.pages.flatMap((p) => p.rows) ?? [];
  const total = pages.data?.pages[0]?.total ?? 0;
  // Click a column to sort by it; again to reverse.
  const sortBy = (key: SortKey) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" || key === "phone" ? "asc" : "desc" }));
  const Th = ({ k, children, className }: { k?: SortKey; children: ReactNode; className?: string }) => (
    <th className={className} aria-sort={k && sort.key === k ? (sort.dir === "asc" ? "ascending" : "descending") : undefined}>
      {k ? (
        <button type="button" onClick={() => sortBy(k)} className="inline-flex items-center gap-1 font-semibold hover:text-ink">
          {children}
          <span className="text-muted">{sort.key === k ? (sort.dir === "asc" ? "▲" : "▼") : ""}</span>
        </button>
      ) : (
        children
      )}
    </th>
  );

  return (
    <div className="max-w-5xl">
      <PageHeader
        title="Clients"
        description="Search by name, mobile, email, or date of birth. Click a column to sort."
        action={
          <div className="flex gap-2">
            {can("settings.manage") && (
              <Link to="/clients/import/visits" className="rounded-[var(--radius-control)] border border-line bg-white px-4 py-2 font-semibold hover:border-brand">
                Import past visits
              </Link>
            )}
            {can("clients.edit") && (
              <Button variant="primary" onClick={() => setAdding(true)}>
                Add client
              </Button>
            )}
          </div>
        }
      />
      <div className="mb-4 flex flex-col gap-3">
        <input
          type="search"
          {...searchInputProps}
          autoFocus
          aria-label="Search clients"
          placeholder="Name, phone, email, or 4/2/1990"
          className="w-full rounded-[var(--radius-control)] border border-line bg-white px-4 py-3 text-base focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-ink-soft">
          <label className="flex items-center gap-2">
            Sort
            <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1" value={sort.key} onChange={(e) => sortBy(e.target.value as SortKey)}>
              {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABELS[k]}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            Tag
            <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1" value={tagId} onChange={(e) => setTagId(e.target.value)}>
              <option value="">Any</option>
              {tags.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={upcoming} onChange={(e) => setUpcoming(e.target.checked)} />
            Upcoming visit
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" className="size-4 accent-[var(--color-brand)]" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
            Archived
          </label>
          {pages.data && <span className="ml-auto text-muted">{total === 1 ? "1 client" : `${total.toLocaleString()} clients`}</span>}
        </div>
      </div>
      {pages.isLoading ? (
        <Loading />
      ) : pages.error ? (
        <Alert>{errorMessage(pages.error)}</Alert>
      ) : rows.length === 0 ? (
        <Alert tone="info">{debounced || tagId || upcoming || archived ? "No clients match." : "No clients yet."}</Alert>
      ) : (
        <>
          {/* Phones: a simple list. */}
          <Panel className="sm:hidden">
            <ul className="divide-y divide-mist">
              {rows.map((c) => (
                <li key={c.id}>
                  <Link to={`/clients/${c.id}`} className="flex flex-col gap-1 px-4 py-3 hover:bg-mist/50">
                    <span className="font-semibold">
                      {displayName(c)} {c.status === "lead" && <span className="text-xs font-normal text-muted">(lead)</span>}
                    </span>
                    <span className="text-sm text-muted">
                      {c.mobile ? formatPhone(c.mobile) : "No phone"} · Last visit {fmtVisit(c.lastVisit)}
                    </span>
                    <TagChips ids={c.tagIds} tags={tags.data} />
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel className="hidden overflow-x-auto sm:block">
            <table className="table-base">
              <thead>
                <tr>
                  <Th k="name">Name</Th>
                  <Th k="phone">Phone</Th>
                  <Th k="lastVisit">Last visit</Th>
                  <Th>Tags</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="hover:bg-mist/40">
                    <td>
                      <Link to={`/clients/${c.id}`} className="font-semibold hover:text-brand">
                        {displayName(c)}
                      </Link>
                      {c.status === "lead" && <span className="ml-1 text-xs text-muted">(lead)</span>}
                      {c.dob && ageOn(c.dob, today()) < 18 && <span className="ml-2 rounded-full bg-wait-wash px-2 py-0.5 text-xs font-semibold text-wait">Under 18</span>}
                    </td>
                    <td className="whitespace-nowrap">{c.mobile ? formatPhone(c.mobile) : <span className="text-muted">—</span>}</td>
                    <td className="whitespace-nowrap">{fmtVisit(c.lastVisit)}</td>
                    <td>
                      <TagChips ids={c.tagIds} tags={tags.data} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
          {pages.hasNextPage && (
            <div className="mt-3 flex justify-center">
              <Button variant="secondary" busy={pages.isFetchingNextPage} onClick={() => pages.fetchNextPage()}>
                Show more ({(total - rows.length).toLocaleString()} more)
              </Button>
            </div>
          )}
        </>
      )}
      {adding && <AddClientDialog mobile={params.get("add") || null} callerId={params.get("name")} onClose={() => setAdding(false)} />}
    </div>
  );
}

const blank: ClientInput = {
  firstName: "",
  lastName: "",
  preferredName: null,
  dob: "",
  sexAtBirth: null,
  pronouns: null,
  mobile: null,
  email: null,
  emergencyName: null,
  emergencyRelationship: null,
  emergencyPhone: null,
  schedulingAlert: null,
  addresses: [],
};

function AddClientDialog({ mobile, callerId, onClose }: { mobile?: string | null; callerId?: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const tags = useTags();
  // A caller ID name is only a guess (and carriers differ on which name comes first): staff confirm it on the call.
  const guess = callerIdName(callerId);
  const [v, setV] = useState<ClientInput>({ ...blank, mobile: mobile ? formatPhone(mobile) : null, ...(guess ?? {}) });
  const [dupes, setDupes] = useState<DuplicateCandidate[] | null>(null);
  const set = <K extends keyof ClientInput>(k: K, value: ClientInput[K]) => {
    setDupes(null);
    setV((cur) => ({ ...cur, [k]: value }));
  };
  const check = useMutation({
    mutationFn: () => post<DuplicateCandidate[]>("/api/clients/duplicates", { firstName: v.firstName, lastName: v.lastName, dob: v.dob || null, mobile: v.mobile, email: v.email }),
  });
  const create = useMutation({
    mutationFn: () => post<ClientDetail>("/api/clients", v),
    onSuccess: async (c) => {
      await qc.invalidateQueries({ queryKey: ["clients"] });
      onClose();
      navigate(`/clients/${c.id}`);
    },
  });
  const submit = async () => {
    if (dupes === null) {
      const found = await check.mutateAsync();
      if (found.length) {
        setDupes(found);
        return;
      }
    }
    create.mutate();
  };
  const reasonText = (r: DuplicateCandidate["reasons"][number]) => (r === "same_dob_name" ? "same birthday and name" : r === "same_mobile" ? "same mobile (family member?)" : "same email (family member?)");

  return (
    <Dialog
      title="Add client"
      onClose={onClose}
      wide
      footer={
        <>
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="client-form" busy={check.isPending || create.isPending}>
            {dupes?.length ? "Add anyway" : "Add client"}
          </Button>
        </>
      }
    >
      <form id="client-form" className="flex flex-col gap-4" onSubmit={(e) => (e.preventDefault(), void submit())}>
        {(check.error || create.error) && <Alert>{errorMessage(check.error ?? create.error)}</Alert>}
        {dupes?.length ? (
          <div className="rounded-[var(--radius-control)] border border-wait/40 bg-wait-wash">
            <p className="px-4 pt-3 text-sm font-semibold text-wait">Is it one of these? Open their record instead of adding a duplicate.</p>
            <ul className="divide-y divide-wait/20">
              {dupes.map((d) => (
                <li key={d.client.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
                  <span>
                    <Link to={`/clients/${d.client.id}`} onClick={onClose} className="font-semibold underline">
                      {displayName(d.client)}
                    </Link>{" "}
                    <span className="text-sm">
                      {fmtDob(d.client.dob)}
                      {d.client.mobile && ` · ${formatPhone(d.client.mobile)}`}
                    </span>
                    <span className="block text-xs text-wait">{d.reasons.map(reasonText).join(", ")}</span>
                  </span>
                  <TagChips ids={d.client.tagIds} tags={tags.data} />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {guess && (
          <p className="text-sm text-ink-soft">
            Name from caller ID ({callerId}): check it with them.{" "}
            <button type="button" className="font-semibold text-brand underline" onClick={() => setV((cur) => ({ ...cur, firstName: cur.lastName, lastName: cur.firstName }))}>
              Swap first and last
            </button>
          </p>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First name" required autoFocus maxLength={80} value={v.firstName} onChange={(e) => set("firstName", e.target.value)} />
          <Field label="Last name" required maxLength={80} value={v.lastName} onChange={(e) => set("lastName", e.target.value)} />
          <DateOfBirthField required value={v.dob} onChange={(d) => set("dob", d)} />
          <SelectField label="Sex assigned at birth" value={v.sexAtBirth ?? ""} onChange={(e) => set("sexAtBirth", (e.target.value || null) as ClientInput["sexAtBirth"])}>
            <option value="">—</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="intersex">Intersex</option>
            <option value="unknown">Prefer not to say</option>
          </SelectField>
          <Field label="Mobile" type="tel" value={v.mobile ?? ""} onChange={(e) => set("mobile", e.target.value || null)} hint="Can be shared with family." />
          <Field label="Email" type="email" value={v.email ?? ""} onChange={(e) => set("email", e.target.value || null)} />
        </div>
        {v.dob && ageOn(v.dob, today()) < 18 && <Alert tone="info">Under 18: link a parent or guardian on their record after adding them.</Alert>}
      </form>
    </Dialog>
  );
}
